import React, { useEffect, useState, useContext, useRef } from "react";
import { HiArrowSmLeft } from "react-icons/hi";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
import { FaInfoCircle } from "react-icons/fa";
import { useProfile } from "../../context/ProfileContext";
import ProductDrawer, { looksLikeBarcode } from "../inventory/ProductDrawer";
import config from "../../config";

/**
 * ProductCatalog Component
 * 
 * A reusable product catalog drawer with cart functionality
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls drawer visibility
 * @param {Function} props.onClose - Callback when drawer closes
 * @param {Array} props.initialCart - Initial cart items [{id, quantity, ...}]
 * @param {Function} props.onCartUpdate - Callback when cart updates with new cart array
 * @param {Function} props.onAddToCart - Alternative callback when user adds to cart (receives cart items)
 * @param {string} props.apiEndpoint - Custom API endpoint (optional)
 * @param {Object} props.filters - Additional filters for API call (optional)
 * @param {boolean} props.isFullWidth - Whether drawer should be full width
 */
function ProductCatalog({
  isOpen,
  onClose,
  initialCart = [],
  onCartUpdate,
  onAddToCart,
  apiEndpoint = "/vendorproduct/getAvailableInventoryProducts",
  filters = {},
  isFullWidth = false,
  drawerWidth = null,
  vendorId, 
}) {
  // 👇 Get profile data to check system settings
  const { profileData } = useProfile();
  
  // 👇 Check if negative inventory is allowed from system settings
  const allowNegativeInventory = !!profileData?.systemSettings?.allow_negative_inventory_billing;

  // State
  const [allProducts, setAllProducts] = useState([]); // 👈 NEW: Store all fetched products
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [catalogCart, setCatalogCart] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [tooltipOpenId, setTooltipOpenId] = useState(null);
  const [scanning, setScanning] = useState(false);
  const searchInputRef = useRef(null);
  const [showProductDrawer, setShowProductDrawer] = useState(false);
  const [newProductName, setNewProductName] = useState("");
  const [initialBarcode, setInitialBarcode] = useState("");

  // Initialize cart from initialCart when drawer opens
  useEffect(() => {
    if (isOpen) {
      const cartMap = {};
      initialCart.forEach((item) => {
        if (item.id) {
          cartMap[item.id] = Number(item.quantity || 0);
        }
      });
      setCatalogCart(cartMap);
      setSearchQuery("");
      
      // Fetch products if needed
      if (allProducts.length === 0) {
        fetchProducts();
      } else {
        applyFilter(allProducts, "");
      }

      // Focus search for barcode scanners
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

    const handleAddNewProduct = () => {
      const raw = searchQuery.trim();
      const cleaned = raw.replace(/[^0-9a-z]/gi, "");
      if (looksLikeBarcode(raw) || looksLikeBarcode(cleaned)) {
        setNewProductName("");
        setInitialBarcode(cleaned || raw);
      } else {
        setInitialBarcode("");
        setNewProductName(raw);
      }
      setShowProductDrawer(true);
    };

    // Called when ProductDrawer saves a new product successfully
    const handleProductDrawerSave = (savedProduct) => {
      setShowProductDrawer(false);
      setNewProductName("");
      setInitialBarcode("");     // ← ADD
      // Refresh catalog so the new product shows up, then re-run the search
      fetchProducts();
    };

  // Fetch products from API
  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await httpService.get(apiEndpoint, { params: filters });
      const fetchedProducts = res.data?.data || res.data || [];
      
      const processedProducts = fetchedProducts.map(processProduct);
      
      // 👇 Store ALL products
      setAllProducts(processedProducts);
      
      // 👇 Apply initial filter based on allowNegativeInventory and search
      applyFilter(processedProducts, searchQuery);
      
    } catch (err) {
      console.error("Failed to load products catalog", err);
      setError("Failed to load products. Please try again.");
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  // 👇 NEW: Apply filter based on search and allowNegativeInventory
  const applyFilter = (products, searchTerm) => {
    const query = searchTerm.toLowerCase().trim();
    
    let filtered = products;
    
    // If no search and negative inventory is OFF -> show only in-stock products
    if (!query && !allowNegativeInventory) {
      filtered = products.filter(p => p.maxAvailable > 0);
    } 
    // If searching -> show ALL matching products (including out-of-stock)
    else if (query) {
      filtered = products.filter(p => 
        p.name?.toLowerCase().includes(query) ||
        p.sku_number?.toLowerCase().includes(query) ||
        String(p.barcode || "").toLowerCase().includes(query) ||
        p.category?.toLowerCase().includes(query)
      );
    }
    // If negative inventory is ON -> show ALL products (already have all)
    
    setCatalogProducts(filtered);
  };

  // Process product data (GST calculations, image URL, etc.)
  const processProduct = (product) => {
    const numerator = parseFloat(product.sale_price_after_discount) * parseFloat(product.gst_slab || 0);
    const numeratorbeforediscount = parseFloat(product.sale_price) * parseFloat(product.gst_slab || 0);
    const denominator = 100 + Number(product.gst_slab || 0);
    const gstAmount = denominator ? numeratorbeforediscount / denominator : 0;
    const gstAmountAfterDiscount = denominator ? numerator / denominator : 0;
    const beforeGST = parseFloat(product.sale_price_after_discount) - parseFloat(gstAmount || 0);
    
    const image = product?.images?.length > 0
      ? `${config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL}/${product.images[0]}`
      : "https://placehold.co/300x300/cccccc/ffffff.png?text=No+Image";

    // 👇 Get stock from quantity_available
    const stock = Number(product.quantity_available ?? product.available_quantity ?? 0);

    return {
      ...product,
      image,
      _id: product._id ?? product.id,
      id: product._id || product.id,
      beforeGST: parseFloat(beforeGST),
      gstAmount: parseFloat(gstAmount || 0),
      gstAmountAfterDiscount: parseFloat(gstAmountAfterDiscount || 0),
      afterGST: parseFloat(product.sale_price_after_discount || 0),
      maxAvailable: stock,
      isOutOfStock: stock <= 0,
      discount_amount: product.discount_amount,
      sale_price: product.sale_price,
      name: product.name ?? product.product_name ?? product.title ?? "Unnamed",
      price: Number(product.sale_price_after_discount ?? product.afterGST ?? product.price ?? 0),
    };
  };

  // ✅ UPDATED: Update quantity with stock validation
  const updateCartQuantity = (productId, quantity) => {
    const newQuantity = Math.floor(Number(quantity) || 0);
    const product = allProducts.find((p) => p.id === productId); // 👈 Use allProducts

    // Prevent negative quantity
    if (newQuantity < 0) return;

    // Validation only when negative inventory is NOT allowed
    if (!allowNegativeInventory && product) {
      // Stock is zero or negative
      if (product.maxAvailable <= 0 && newQuantity > 0) {
        toast.error(`${product.name} is out of stock.`);
        return;
      }

      // Cannot exceed available stock
      if (newQuantity > product.maxAvailable) {
        toast.error(
          `Cannot exceed available stock for ${product.name} (${product.maxAvailable} available)`
        );
        return;
      }
    }

    // Only show warning when negative inventory is allowed
    if (
      allowNegativeInventory &&
      product &&
      newQuantity > product.maxAvailable
    ) {
      toast.warning(
        `Quantity exceeds available stock for ${product.name} (${product.maxAvailable} available)`
      );
    }

    setCatalogCart((prev) => ({
      ...prev,
      [productId]: newQuantity,
    }));
  };

  // ✅ UPDATED: Increment/decrement with stock validation
  const adjustQuantity = (productId, delta) => {
    const currentQty = catalogCart[productId] || 0;
    const newQty = currentQty + delta;
    const product = allProducts.find((p) => p.id === productId);

    if (newQty < 0) return;

    // Validation only when negative inventory is NOT allowed
    if (!allowNegativeInventory && product) {
      // Stock is zero or negative
      if (product.maxAvailable <= 0 && newQty > 0) {
        toast.error(`${product.name} is out of stock.`);
        return;
      }

      // Cannot exceed available stock
      if (newQty > product.maxAvailable) {
        toast.error(
          `Cannot exceed available stock for ${product.name} (${product.maxAvailable} available)`
        );
        return;
      }
    }

    // Warning only when negative inventory is allowed
    if (
      allowNegativeInventory &&
      product &&
      newQty > product.maxAvailable
    ) {
      toast.warning(
        `Quantity exceeds available stock for ${product.name} (${product.maxAvailable} available)`
      );
    }

    updateCartQuantity(productId, newQty);
  };

  // 👇 NEW: Handle search with filtering
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Apply filter based on search and allowNegativeInventory
    if (allProducts.length > 0) {
      applyFilter(allProducts, query);
    }
  };

  const findExactCodeMatch = (products, code) => {
    const q = String(code || "").trim().toLowerCase();
    if (!q) return [];
    return (products || []).filter((p) => {
      const barcode = String(p.barcode || "").trim().toLowerCase();
      const sku = String(p.sku_number || p.sku || "").trim().toLowerCase();
      return barcode === q || sku === q;
    });
  };

  const addScannedProduct = (product) => {
    if (!product?.id) return false;

    if (!allowNegativeInventory && product.maxAvailable <= 0) {
      toast.error(`${product.name} is out of stock.`);
      return false;
    }

    const currentQty = catalogCart[product.id] || 0;
    const nextQty = currentQty + 1;

    if (!allowNegativeInventory && nextQty > product.maxAvailable) {
      toast.error(
        `Cannot exceed available stock for ${product.name} (${product.maxAvailable} available)`,
      );
      return false;
    }

    if (allowNegativeInventory && nextQty > product.maxAvailable) {
      toast.warning(
        `Quantity exceeds available stock for ${product.name} (${product.maxAvailable} available)`,
      );
    }

    setCatalogCart((prev) => ({
      ...prev,
      [product.id]: nextQty,
    }));
    toast.success(`Added ${product.name}`);
    return true;
  };

  /** POS-like: barcode scanners send code + Enter → auto-add matching product */
  const handleSearchKeyDown = async (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();

    const code = searchQuery.trim();
    if (!code || scanning) return;

    setScanning(true);
    try {
      // 1) Exact barcode / SKU match from loaded catalog
      let matches = findExactCodeMatch(allProducts, code);

      // 2) If not found locally, search API (same as POS)
      if (matches.length === 0) {
        try {
          const res = await httpService.get(
            `/vendorproduct/searchAvailabelVendorProducts?fromSale=true&search=${encodeURIComponent(code)}`,
          );
          const results = res?.data?.data || res?.data || res || [];
          const processed = (Array.isArray(results) ? results : []).map(processProduct);
          matches = findExactCodeMatch(processed, code);
          if (matches.length === 0 && processed.length === 1) {
            matches = processed;
          }
          // Merge any new products into local catalog for qty updates
          if (processed.length > 0) {
            setAllProducts((prev) => {
              const byId = new Map(prev.map((p) => [String(p.id), p]));
              processed.forEach((p) => {
                if (p.id && !byId.has(String(p.id))) byId.set(String(p.id), p);
              });
              return Array.from(byId.values());
            });
          }
        } catch (err) {
          console.error("Barcode search failed:", err);
        }
      }

      if (matches.length === 1) {
        const ok = addScannedProduct(matches[0]);
        if (ok) {
          setSearchQuery("");
          applyFilter(allProducts, "");
          setTimeout(() => searchInputRef.current?.focus(), 50);
        }
        return;
      }

      if (matches.length > 1) {
        setCatalogProducts(matches);
        toast.info("Multiple products match — select one");
        return;
      }

      // Fall back to normal name/sku/barcode contains filter
      const q = code.toLowerCase();
      const softMatches = allProducts.filter(
        (p) =>
          p.name?.toLowerCase().includes(q) ||
          p.sku_number?.toLowerCase().includes(q) ||
          String(p.barcode || "").toLowerCase().includes(q) ||
          p.category?.toLowerCase().includes(q),
      );
      setCatalogProducts(softMatches);
      if (softMatches.length === 0) {
        toast.warning(`No product found for "${code}"`);
      }
    } finally {
      setScanning(false);
    }
  };

  // Calculate total items in cart
  const getTotalItems = () => {
    return Object.values(catalogCart).reduce((sum, qty) => sum + qty, 0);
  };

  // Calculate total price
  const getTotalPrice = () => {
    return Object.entries(catalogCart).reduce((sum, [productId, qty]) => {
      const product = allProducts.find(p => p.id === productId);
      if (!product || qty === 0) return sum;
      const effectivePrice = Number(product.effective_sale_price || product.mrp_price || 0);
      return sum + (effectivePrice * qty);
    }, 0);
  };

  // Handle adding to cart
  const handleAddToCart = () => {
    // Convert cart map to array of items with quantities > 0
    const cartItems = Object.entries(catalogCart)
      .filter(([_, qty]) => qty > 0)
      .map(([productId, quantity]) => {
        const product = allProducts.find(p => p.id === productId);
        return {
          ...product,
          quantity,
        };
      });

    // Call the appropriate callback
    if (onCartUpdate) {
      onCartUpdate(cartItems);
    }
    
    if (onAddToCart) {
      onAddToCart(cartItems);
    }

    onClose();
  };

  // Handle clear cart
  const handleClearCart = () => {
    setCatalogCart({});
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className={`absolute inset-y-0 right-0 flex w-full ${!isFullWidth ? drawerWidth || "lg:w-3/4" : ""}`}>
        <div className="relative w-full">
          <div className="flex h-full flex-col bg-white shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b px-4 py-4 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center">
                <button
                  onClick={onClose}
                  className="mr-3 flex h-10 w-10 items-center justify-center rounded-full hover:bg-white/70"
                  aria-label="Close catalog"
                >
                  <HiArrowSmLeft className="h-6 w-6 text-gray-700" />
                </button>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Add Products</h2>
                  <p className="text-sm text-gray-500">
                    {getTotalItems()} item{getTotalItems() !== 1 ? 's' : ''} in cart
                  </p>
                </div>
              </div>
              
              {getTotalItems() > 0 && (
                <button
                  onClick={handleClearCart}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Clear All
                </button>
              )}
            </div>

            {/* Search / barcode scan */}
            <div className="border-b p-4">
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Scan barcode or search by name / SKU..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  autoComplete="off"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 pl-10 focus:border-blue-500 focus:ring-blue-500"
                />
                <div className="absolute left-3 top-2.5 text-gray-400">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {scanning ? (
                  <span>Looking up barcode…</span>
                ) : !searchQuery && !allowNegativeInventory ? (
                  <span>Scan a barcode to add instantly, or search by name. Showing in-stock products.</span>
                ) : !searchQuery && allowNegativeInventory ? (
                  <span>Scan a barcode to add instantly, or search by name.</span>
                ) : catalogProducts.length > 0 ? (
                  <span>Search results: {catalogProducts.length} product(s) found. Press Enter to add exact barcode/SKU match.</span>
                ) : (
                  <span>No products found for "{searchQuery}"</span>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {loading ? (
                <div className="flex h-64 items-center justify-center">
                  <div className="text-center">
                    <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
                    <p className="mt-2 text-gray-500">Loading products...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="flex h-64 flex-col items-center justify-center">
                  <div className="rounded-full bg-red-100 p-3">
                    <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="mt-4 text-gray-700">{error}</p>
                  <button
                    onClick={fetchProducts}
                    className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                  >
                    Try Again
                  </button>
                </div>
              ) : catalogProducts.length === 0 ? (
                <div className="flex h-64 flex-col items-center justify-center">
                  <div className="rounded-full bg-gray-100 p-3">
                    <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                  </div>
                  <p className="mt-4 text-gray-500">
                    {searchQuery ? `No products found for "${searchQuery}"` : "No products available"}
                  </p>
                  {!searchQuery && !allowNegativeInventory && (
                    <p className="text-xs text-gray-400 mt-1">
                      Try searching for out-of-stock products
                    </p>
                  )}

                  {/* 👇 offer to create a new product using whatever was typed/scanned */}
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={handleAddNewProduct}
                      className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                    >
                      + Add "{searchQuery}" as New Product
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {catalogProducts.map((product) => {
                    const cartQuantity = catalogCart[product.id] || 0;
                    const isOutOfStock = product.maxAvailable <= 0;
                    
                    return (
                      <div
                        key={product.id}
                        className={`flex items-center gap-3 rounded-lg border p-3 ${
                          isOutOfStock && !allowNegativeInventory
                            ? 'border-gray-200 bg-gray-50 opacity-60' 
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        {/* Product Image */}
                        <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              e.target.src = "https://placehold.co/300x300/cccccc/ffffff.png?text=No+Image";
                            }}
                          />
                        </div>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-medium truncate ${isOutOfStock && !allowNegativeInventory ? 'text-gray-500' : 'text-gray-900'}`}>
                            {product.name}
                          </h3>

                          {/* Price and Stock */}
                          <div className="mt-1 flex items-center gap-2 flex-wrap">
                            {(() => {
                              const mrp = Number(product.mrp || 0);
                              const effectivePrice = Number(product.effective_sale_price || product.mrp_price || 0);
                              const appliedDiscount = product.applied_discount;
                              const hasDiscount = appliedDiscount && effectivePrice < mrp;
                              const isFlatDiscount = appliedDiscount?.type === "FLAT";
                              const savingsPerUnit = mrp - effectivePrice;
                              const discountPercent = appliedDiscount?.value || 0;

                              return (
                                <>
                                  {hasDiscount && !(isOutOfStock && !allowNegativeInventory) ? (
                                    <>
                                      <span className="font-semibold text-green-600">
                                        ₹{effectivePrice.toFixed(2)}
                                      </span>
                                      <span className="text-gray-400 line-through text-xs">
                                        ₹{mrp.toFixed(2)}
                                      </span>
                                      {/* Info tooltip */}
                                      <div className="relative inline-block">
                                        <FaInfoCircle
                                          className="text-gray-500 text-xs cursor-help hover:text-gray-700"
                                          onMouseEnter={() => setTooltipOpenId(product.id)}
                                          onMouseLeave={() => setTooltipOpenId(null)}
                                        />
                                        {tooltipOpenId === product.id && (
                                          <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-auto whitespace-nowrap bg-gray-800 text-white text-xs rounded-lg py-1.5 px-3 shadow-lg">
                                            <div className="flex flex-col gap-1">
                                              <div className="flex items-center gap-2">
                                                <span className="text-gray-300">Discount:</span>
                                                <span className="text-green-400 font-semibold">
                                                  {isFlatDiscount
                                                    ? `₹${savingsPerUnit.toFixed(2)}/unit`
                                                    : `${discountPercent}% OFF (₹${savingsPerUnit.toFixed(2)}/unit)`}
                                                </span>
                                              </div>
                                              {appliedDiscount?.name && (
                                                <div className="flex items-center gap-2 border-t border-gray-600 mt-1 pt-1">
                                                  <span className="text-gray-300">Offer:</span>
                                                  <span className="text-blue-400 font-semibold">
                                                    {appliedDiscount.name}
                                                  </span>
                                                </div>
                                              )}
                                            </div>
                                            {/* Tooltip arrow */}
                                            <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2">
                                              <div className="border-4 border-transparent border-t-gray-800"></div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </>
                                  ) : isOutOfStock && !allowNegativeInventory ? (
                                    <>
                                      <span className="font-semibold text-gray-400 line-through">
                                        ₹{effectivePrice.toFixed(2)}
                                      </span>
                                      <span className="text-gray-400 line-through text-xs">
                                        ₹{mrp.toFixed(2)}
                                      </span>
                                    </>
                                  ) : (
                                    <span className="font-semibold text-blue-600">
                                      ₹{Number(product.effective_sale_price || 0).toFixed(2)}
                                    </span>
                                  )}
                                </>
                              );
                            })()}

                            {/* Stock Badge */}
                            {isOutOfStock && !allowNegativeInventory ? (
                              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                                Out of Stock
                              </span>
                            ) : product.maxAvailable !== Infinity && (
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                product.maxAvailable <= 5 
                                  ? 'bg-red-100 text-red-700' 
                                  : product.maxAvailable <= 20 
                                    ? 'bg-yellow-100 text-yellow-700' 
                                    : 'bg-green-100 text-green-700'
                              }`}>
                                Stock: {product.maxAvailable}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Quantity Controls - Disabled when out of stock and setting OFF */}
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => adjustQuantity(product.id, -1)}
                            disabled={cartQuantity === 0 || (isOutOfStock && !allowNegativeInventory)}
                            className={`h-8 w-8 rounded-full border flex items-center justify-center ${
                              cartQuantity === 0 || (isOutOfStock && !allowNegativeInventory)
                                ? "border-gray-200 text-gray-300 cursor-not-allowed"
                                : "border-gray-300 text-gray-600 hover:bg-gray-50"
                            }`}
                            aria-label="Decrease quantity"
                          >
                            −
                          </button>

                          {/* Input - Disabled when out of stock and setting OFF */}
                          <input
                            type="number"
                            min="0"
                            max={!allowNegativeInventory ? product.maxAvailable : undefined}
                            value={cartQuantity}
                            onChange={(e) => updateCartQuantity(product.id, e.target.value)}
                            disabled={isOutOfStock && !allowNegativeInventory}
                            className={`h-8 w-12 text-center border border-gray-300 rounded-md ${
                              isOutOfStock && !allowNegativeInventory ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            aria-label={`Quantity for ${product.name}`}
                          />

                          <button
                            type="button"
                            onClick={() => adjustQuantity(product.id, 1)}
                            disabled={isOutOfStock && !allowNegativeInventory}
                            className={`h-8 w-8 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 flex items-center justify-center ${
                              isOutOfStock && !allowNegativeInventory ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            aria-label="Increase quantity"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {getTotalItems() > 0 && (
              <div className="border-t bg-gray-50 p-4">
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Items</p>
                    <p className="text-lg font-semibold">{getTotalItems()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Total Amount</p>
                    <p className="text-lg font-semibold text-blue-600">
                      ₹{getTotalPrice().toFixed(2)}
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddToCart}
                    className="flex-1 rounded-lg bg-blue-600 px-4 py-3 font-medium text-white hover:bg-blue-700"
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <ProductDrawer
        isOpen={showProductDrawer}
        onClose={() => {
          setShowProductDrawer(false);
          setNewProductName("");
          setInitialBarcode("");
        }}
        onSave={handleProductDrawerSave}
        newProductName={newProductName}
        initialBarcode={initialBarcode}
        vendorId={vendorId}
      />
    </div>
  );
}

export default ProductCatalog;